import React, { useMemo, useState, useRef } from 'react';
import { Drawer, Text, Group, Badge, Stack, Divider, SegmentedControl, Loader, Table, ScrollArea, ActionIcon, Paper, Anchor, useComputedColorScheme } from '@mantine/core';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';

// Decision-transparency canvas: a hand-authored SVG of the /explain graph. The spine climbs
// bottom-left → top-right through the stages, colored by engine: `det` = deterministic C# (gray),
// `ai` = an LLM stage (violet), `agent` = Nymeria (grape). Green = an internal handoff (Context,
// Score); amber = an output the rep sees (summary, priority, draft, recommendation), drawn as a
// callout to the left. Data inputs form a triangle right of the Context box; guidance sits below Nymeria.
// Edges touch the box SIDES (no handles). Click a node → the drawer with what was used.
// `line` and `note` are used only by the DEAL-level canvas (DealCanvas): a collapsed line branch, and the
// explanatory placeholder shown where a single-line deal has no deal summary.
export const TYPE_BG = { data: '#e7f5ff', guidance: '#fff0f6', det: '#f1f3f5', ai: '#f3f0ff', product: '#ebfbee', surfaced: '#fff4e6', agent: '#f8f0fc', line: '#e6fcf5', note: '#f8f9fa' };
export const TYPE_BD = { data: '#4dabf7', guidance: '#f06595', det: '#868e96', ai: '#7048e8', product: '#40c057', surfaced: '#f76707', agent: '#ae3ec9', line: '#0ca678', note: '#ced4da' };
// A glyph in each box's top-right corner marks its role. EVERY node type has one, and every one of them is
// mirrored in the legend below the canvas and in the docs (#decision-trace) — the glyph is how a reader gets
// from a box on screen to the row that explains it, so a type with no glyph, or a legend swatch with no
// glyph, breaks that link.
//   ▤ data       — a fact we looked up            § guidance   — a rule leadership wrote
//   ⚙ det        — deterministic step             ✳ ai         — an LLM step (costs money, can be wrong)
//   ✦ agent      — Nymeria                        ▸ product    — internal handoff to the next step
//   ◉ surfaced   — a result the rep actually sees
//   ◆ line       — one deal line   (DealCanvas)   ⓘ note       — an explanatory placeholder (DealCanvas)
export const ICON = {
  data: '▤', guidance: '§', det: '⚙', ai: '✳', product: '▸', surfaced: '◉', agent: '✦',
  line: '◆', note: 'ⓘ',
};
// THIS CANVAS IS STRICTLY LINE GRAIN. Every node describes the ONE line the rep clicked; nothing computed
// across lines may appear. Deal-grain work is drawn in DealCanvas instead — that separation is what lets both
// be honest, and it is why `line_summary` can be here while `deal_summary` cannot:
//   line_summary — an AI step that burned tokens on THIS line (LineSummaryStage sees exactly one line). It
//     replaced the old deal-grain `summarize` node, which wrote prose about the deal's OTHER parts and was
//     therefore pulled from this canvas — which is how the AI step became invisible while it kept billing.
//   deal_summary — a synthesis across every line. Cannot be reduced to one. Lives in DealCanvas.
//   customer_brief — ACCOUNT grain, so it applies identically to every line: drawn as a shared INPUT, like
//     the guidance nodes, not as a step in this line's chain.
const STAGE_ORDER = ['context', 'score', 'pricing', 'line_summary', 'judgement', 'recommendation'];
// Spine climbs right by DX / up by DY, with green outputs inline between the stages. Orange
// "shown to rep" results are pulled far left, left-justified above the Context box.
const X0 = 60, TOPY = 40, DX = 85, DY = 56;
const isStage = (t) => t === 'det' || t === 'ai' || t === 'agent';
const widthOf = (n) => (isStage(n?.type) ? 190 : n?.type === 'surfaced' ? 170 : n?.type === 'product' ? 180 : n?.type === 'data' ? 185 : n?.type === 'guidance' ? 160 : 160);
const enc = encodeURIComponent;

// Wrap a label to the box width (rough monospace-ish estimate); returns the lines.
export function wrapLabel(label, w) {
  const max = Math.max(6, Math.floor((w - 18) / 6.4));
  const words = String(label || '').split(/\s+/);
  const lines = [];
  let cur = '';
  words.forEach((word) => {
    const t = cur ? `${cur} ${word}` : word;
    if (t.length > max && cur) { lines.push(cur); cur = word; } else cur = t;
  });
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

// Point on a box's border along the direction toward (tx, ty) — so edges touch the facing side.
export function anchor(p, tx, ty) {
  const cx = p.x + p.w / 2, cy = p.y + p.h / 2;
  const dx = tx - cx, dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const sx = dx !== 0 ? (p.w / 2) / Math.abs(dx) : Infinity;
  const sy = dy !== 0 ? (p.h / 2) / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
}

// Compute a position + size for every node. Returns { placed: {id: {x,y,w,h,lines,node}} }.
function computeLayout(ns) {
  const byId = Object.fromEntries(ns.map((n) => [n.id, n]));
  const dim = {};
  ns.forEach((n) => {
    const w = widthOf(n); const lines = wrapLabel(n.label, w);
    // Green outputs are wide enough for one line but short (secondary vs the taller purple stages).
    const h = n.type === 'product' ? Math.max(30, 8 + lines.length * 13) : Math.max(46, 16 + lines.length * 15);
    dim[n.id] = { w, h, lines };
  });
  const pos = {};

  // MAIN DIAGONAL — inline spine: stage → its green output → next stage → …, climbing bottom-left → top-right.
  const spine = [];
  STAGE_ORDER.forEach((sid) => {
    if (!byId[sid]) return;
    spine.push(sid);
    const out = ns.find((n) => n.parent === sid && n.type === 'product');   // green intermediate, inline
    if (out) spine.push(out.id);
  });
  const N = spine.length;
  spine.forEach((id, i) => { pos[id] = { x: X0 + i * DX, y: TOPY + (N - 1 - i) * DY }; });
  const at = (id) => pos[id] || null;

  // Orange "shown to rep" results extend out to the left, left-justified above the Context box (filling the
  // open upper-left space), at the height of the stage that produced them.
  ns.filter((n) => n.type === 'surfaced').forEach((n) => {
    const p = at(n.parent); if (!p) return;
    pos[n.id] = { x: X0, y: p.y + (dim[n.parent].h - dim[n.id].h) / 2 };
  });

  // Data: interlocking triangle tucked tight against the Context box's right side (short feed lines;
  // columns 1, 2, 3 … high, bottom row at the Context box's height).
  const s1 = at('context') || { x: X0, y: TOPY + (N - 1) * DY };
  const s1w = (dim.context || {}).w || 190;
  const dataNodes = ns.filter((n) => n.type === 'data' && !pos[n.id]);
  for (let di = 0, col = 0; di < dataNodes.length; col++) {
    for (let r = 0; r <= col && di < dataNodes.length; r++, di++) {
      const dw = dim[dataNodes[di].id].w;
      // Whole triangle nudged right ~20% of a block; each higher row staggered a further ~40%.
      pos[dataNodes[di].id] = { x: s1.x + s1w + 24 + col * 200 + dw * (0.20 + r * 0.40), y: s1.y - r * 78 };
    }
  }
  // SIDE INPUTS — the shared, ACCOUNT-grain things that feed this line's branch without being steps in it:
  // the guidance rules, and the customer brief. They are identical on every line of the deal, which is exactly
  // why they hang off the spine instead of sitting on it.
  //
  // The customer brief belongs here for the same reason the guidance does. It is an input; the only difference
  // is that an earlier AI stage wrote it rather than a table holding it (hence violet, not pink). It has no
  // business in the diagonal, and anchoring it to the data triangle just made it land on top of the guidance.
  //
  // One column below Nymeria: it cannot collide with the diagonal spine or the data triangle, and it grows
  // upward, so adding an entry never pushes anything into the rows below.
  const reco = at('recommendation') || at('deal_summary') || at('judgement') || { x: X0 + (N - 1) * DX, y: TOPY };
  const rh = (dim.recommendation || {}).h || 46;
  const brief = ns.find((n) => n.id === 'customer_brief' && !pos[n.id]);
  const sideInputs = ns.filter((n) => n.type === 'guidance' && !pos[n.id]);
  // advisor last (bottom); climb upward so a gated/absent Nymeria node can't push the stack down into
  // the data row.
  sideInputs.sort((a, b) => (/advisor/i.test(a.label) ? 1 : 0) - (/advisor/i.test(b.label) ? 1 : 0));
  if (brief) sideInputs.unshift(brief);   // topmost — it is read before any rule is applied
  if (sideInputs.length) {
    const gGap = 14;
    // Clear the ENTIRE spine, not just the anchor node. Deriving x from the recommendation box was fragile:
    // on a deal Nymeria never ran, the anchor falls back to Judgement, the column climbs to Judgement's own
    // height, and the top of the stack lands on top of Step 4. Taking the rightmost edge of every spine node
    // means no stage box can be overlapped, whichever stages happen to exist for this line.
    const spineRight = spine.length
      ? Math.max(...spine.map((id) => pos[id].x + dim[id].w))
      : reco.x + ((dim.recommendation || {}).w || 190);
    const gx = spineRight + 40;
    let gy = reco.y + rh + 130;   // advisor's row (bottom of the stack); the rest climb above it
    for (let i = sideInputs.length - 1; i >= 0; i -= 1) {
      pos[sideInputs[i].id] = { x: gx, y: gy };
      gy -= dim[sideInputs[i].id].h + gGap;
    }
  }
  // safety net
  ns.filter((n) => !pos[n.id]).forEach((n, k) => { pos[n.id] = { x: s1.x + k * 220, y: s1.y + 200 }; });

  const placed = {};
  ns.forEach((n) => { const p = pos[n.id] || { x: 0, y: 0 }; placed[n.id] = { ...p, ...dim[n.id], node: n }; });
  return { placed };
}

// The trace is drawn for ONE line: Vesper judges each line on its own, so the Judgement / Priority /
// Nymeria nodes all reflect the selected line (keyed by dealLineId, the pipeline grain).
export default function DecisionCanvas({ dealPublicId, dealLineId, partNumber, conditionCode, partClass }) {
  const url = dealPublicId
    ? `${config.apiBaseUrl}/api/vesper/deals/${enc(dealPublicId)}/explain?${new URLSearchParams({
      ...(dealLineId ? { dealLineId: String(dealLineId) } : {}),
      ...(partNumber ? { partNumber } : {}),
    }).toString()}`
    : null;
  const { data, isLoading } = useAuthenticatedData(url);
  const [sel, setSel] = useState(null);

  const { placed, edges } = useMemo(() => {
    const ns = data?.nodes || [];
    return { placed: computeLayout(ns).placed, edges: data?.edges || [] };
  }, [data]);

  if (isLoading) return <Group justify="center" p="xl"><Loader /></Group>;
  if (!data?.meta?.enriched) return <Text c="dimmed" p="md">Not enriched yet — enrich the deal to see its decision trace.</Text>;

  return (
    <>
      <Group gap="lg" mb="xs" px="xs" wrap="wrap">
        {/* Every swatch carries its glyph — the same one drawn on the box. Four of these used to be blank,
            so a reader looking at a ▤ or a ◉ on the canvas had nothing in the legend to match it against. */}
        <LegendItem color={TYPE_BD.data} label="Inputs (data)" icon={ICON.data} />
        <LegendItem color={TYPE_BD.guidance} label="Guidance" icon={ICON.guidance} />
        <LegendItem color={TYPE_BD.det} label="Deterministic stage" icon={ICON.det} bold />
        <LegendItem color={TYPE_BD.ai} label="AI stage" icon={ICON.ai} bold />
        <LegendItem color={TYPE_BD.agent} label="AI Sales Agent" icon={ICON.agent} bold />
        <LegendItem color={TYPE_BD.product} label="Output → next stage" icon={ICON.product} />
        <LegendItem color={TYPE_BD.surfaced} label="Shown to rep" icon={ICON.surfaced} bold />
      </Group>
      <DecisionGraph placed={placed} edges={edges} onNodeClick={setSel} />
      <NodeDrawer dealPublicId={dealPublicId} partNumber={partNumber} conditionCode={conditionCode}
        partClass={partClass} node={sel} onClose={() => setSel(null)} />
    </>
  );
}

// The SVG canvas: draws the placed nodes + side-anchored edges, with wheel-zoom / drag-pan.
export function DecisionGraph({ placed, edges, onNodeClick }) {
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [t, setT] = useState({ k: 1, x: 0, y: 0 });

  const ids = Object.keys(placed);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  ids.forEach((id) => { const p = placed[id]; minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x + p.w); maxY = Math.max(maxY, p.y + p.h); });
  if (!ids.length) { minX = 0; minY = 0; maxX = 100; maxY = 100; }
  const PAD = 70;
  const vb = { x: minX - PAD, y: minY - PAD, w: (maxX - minX) + 2 * PAD, h: (maxY - minY) + 2 * PAD };

  // Dashed "Output" frame around the amber, rep-visible results (the surfaced column, upper-left).
  const surf = ids.map((id) => placed[id]).filter((p) => p.node.type === 'surfaced');
  let outBox = null;
  if (surf.length) {
    let sx = Infinity, sy = Infinity, sX = -Infinity, sY = -Infinity;
    surf.forEach((p) => { sx = Math.min(sx, p.x); sy = Math.min(sy, p.y); sX = Math.max(sX, p.x + p.w); sY = Math.max(sY, p.y + p.h); });
    const m = 16;
    outBox = { x: sx - m, y: sy - m, w: (sX - sx) + 2 * m, h: (sY - sy) + 2 * m };
  }

  // client px → SVG user coords (viewBox space, before the pan/zoom group transform)
  const toUser = (clientX, clientY) => {
    const svg = svgRef.current;
    const ctm = svg.getScreenCTM();
    const pt = svg.createSVGPoint(); pt.x = clientX; pt.y = clientY;
    return pt.matrixTransform(ctm.inverse());
  };
  const onWheel = (e) => {
    if (!svgRef.current) return;
    e.preventDefault();
    const s = toUser(e.clientX, e.clientY);
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    setT((prev) => {
      const k = Math.min(4, Math.max(0.15, prev.k * factor));
      const gx = (s.x - prev.x) / prev.k, gy = (s.y - prev.y) / prev.k;   // keep point under cursor fixed
      return { k, x: s.x - gx * k, y: s.y - gy * k };
    });
  };
  // NB: no setPointerCapture here — capturing the pointer on the <svg> would swallow the click
  // that opens the node drawer. Panning still works via the move handler below.
  const onPointerDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, ox: t.x, oy: t.y }; };
  const onPointerMove = (e) => {
    if (!drag.current || !svgRef.current) return;
    const ctm = svgRef.current.getScreenCTM();
    setT((prev) => ({ ...prev, x: drag.current.ox + (e.clientX - drag.current.x) / ctm.a, y: drag.current.oy + (e.clientY - drag.current.y) / ctm.d }));
  };
  const onPointerUp = () => { drag.current = null; };
  const zoomBy = (f) => setT((prev) => {
    const cx = vb.x + vb.w / 2, cy = vb.y + vb.h / 2;   // zoom about the viewBox center
    const k = Math.min(4, Math.max(0.15, prev.k * f));
    const gx = (cx - prev.x) / prev.k, gy = (cy - prev.y) / prev.k;
    return { k, x: cx - gx * k, y: cy - gy * k };
  });

  return (
    <div style={{ position: 'relative', height: '80vh', width: '100%', background: '#f8f9fa', borderRadius: 8, overflow: 'hidden' }}>
      <svg ref={svgRef} width="100%" height="100%" viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        onWheel={onWheel} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        style={{ cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none' }}>
        <defs>
          <marker id="dt-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="userSpaceOnUse">
            <path d="M0,0 L8,3 L0,6 Z" fill="#868e96" />
          </marker>
        </defs>
        <g transform={`translate(${t.x} ${t.y}) scale(${t.k})`}>
          {outBox && (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={outBox.x} y={outBox.y} width={outBox.w} height={outBox.h} rx="10" ry="10"
                fill="none" stroke={TYPE_BD.surfaced} strokeWidth="1.5" strokeDasharray="6 4" opacity="0.85" />
              <rect x={outBox.x + 14} y={outBox.y - 9} width="56" height="18" rx="4" fill="#f8f9fa" />
              <text x={outBox.x + 22} y={outBox.y + 4} fontSize="12" fontWeight="700" fill={TYPE_BD.surfaced}
                style={{ userSelect: 'none' }}>Output</text>
            </g>
          )}
          {edges.map((e, i) => {
            const a = placed[e.source], b = placed[e.target];
            if (!a || !b) return null;
            const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 }, bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
            const p1 = anchor(a, bc.x, bc.y), p2 = anchor(b, ac.x, ac.y);
            return <line key={`e${i}`} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#868e96" strokeWidth="1" markerEnd="url(#dt-arrow)" />;
          })}
          {ids.map((id) => {
            const p = placed[id], n = p.node;
            const emph = isStage(n.type), surfaced = n.type === 'surfaced';
            const startY = p.h / 2 - ((p.lines.length - 1) * 15) / 2 + 4;
            return (
              <g key={id} transform={`translate(${p.x} ${p.y})`} onClick={() => onNodeClick(n)} style={{ cursor: 'pointer' }}>
                <rect width={p.w} height={p.h} rx="8" ry="8" fill={TYPE_BG[n.type] || '#f8f9fa'}
                  stroke={TYPE_BD[n.type] || '#ced4da'} strokeWidth={emph || surfaced ? 2 : 1} />
                <text x={p.w - 12} y={14} textAnchor="middle" fontSize="12" opacity="0.9"
                  fill={TYPE_BD[n.type] || '#868e96'} style={{ pointerEvents: 'none', userSelect: 'none' }}>{ICON[n.type] || ''}</text>
                <text x={p.w / 2} y={startY} textAnchor="middle" fill="#212529"
                  fontSize={emph ? 13 : 12} fontWeight={emph ? 700 : surfaced ? 600 : 400}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  {p.lines.map((ln, i) => <tspan key={i} x={p.w / 2} dy={i === 0 ? 0 : 15}>{ln}</tspan>)}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <Stack gap={4} style={{ position: 'absolute', left: 12, bottom: 12 }}>
        <ActionIcon variant="default" size="md" onClick={() => zoomBy(1.2)} aria-label="Zoom in">+</ActionIcon>
        <ActionIcon variant="default" size="md" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out">−</ActionIcon>
        <ActionIcon variant="default" size="md" onClick={() => setT({ k: 1, x: 0, y: 0 })} aria-label="Reset view">⤢</ActionIcon>
      </Stack>
    </div>
  );
}

// Small color-key row so the legend explains the pipeline concept: inputs → stages →
// intermediate outputs → the two results surfaced to the rep (amber).
export function LegendItem({ color, label, bold, icon }) {
  return (
    <Group gap={6} wrap="nowrap">
      <span style={{ width: 14, height: 14, borderRadius: 4, background: color, border: bold ? `2px solid ${color}` : 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, lineHeight: 1, color: '#fff' }}>{icon || ''}</span>
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}

function NodeDrawer({ dealPublicId, partNumber, conditionCode, partClass, node, onClose }) {
  const [view, setView] = useState('then');
  const scheme = useComputedColorScheme('light');
  const key = node?.id;
  const drillable = node?.asOf && (key === 'inventory' || key === 'customer');
  const isPricing = key === 'pricing' || key === 'out:pricing';
  const isScore = key === 'score' || key === 'out:score';
  // The summary nodes carry their prose in `node.summary` (there is no structured payload to render), so
  // they take the plain text path and skip the data explorer below.
  const isSummary = key === 'line_summary' || key === 'out:line_summary' || key === 'customer_brief';
  const isJudge = key === 'judgement' || key === 'priority';
  const isReco = key === 'recommendation' || key === 'product_reco';
  const isData = node?.type === 'data';
  const isGuidance = node?.type === 'guidance';
  const pitUrl = (drillable && view === 'now')
    ? `${config.apiBaseUrl}/api/vesper/deals/${enc(dealPublicId)}/explain/node/${key}${partNumber ? `?partNumber=${enc(partNumber)}` : ''}`
    : null;
  const { data: pit } = useAuthenticatedData(pitUrl);
  const shown = view === 'now' ? (pit?.rows ?? pit?.row ?? pit) : node?.data;

  return (
    <Drawer opened={!!node} onClose={onClose} position="right" size="lg"
      title={<Text fw={600} c={scheme === 'dark' ? 'gray.1' : 'dark.8'}>{node?.label}</Text>}>
      {node && (
        <Stack gap="sm">
          {/* Which LINE this drawer is about. Every stage below is line-scoped, and the drawers are opened
              from a graph that doesn't otherwise name the part — so say it, on every drawer. */}
          {partNumber && (
            <Paper withBorder p="xs" radius="md">
              <Group gap={8} wrap="wrap">
                <Text size="sm" fw={700}>{partNumber}</Text>
                <Badge size="xs" variant="light" color="gray">{conditionCode || 'no cond'}</Badge>
                {partClass && <Badge size="xs" variant="light" color="grape">Class {partClass}</Badge>}
              </Group>
            </Paper>
          )}
          <Group gap="xs">
            <Badge variant="light">{node.type}</Badge>
            {node.model && <Badge color="violet" variant="light">{node.model}</Badge>}
            {node.status && <Badge color="gray" variant="light">{node.status}</Badge>}
            {node.tokens && (node.tokens.input || node.tokens.output) && (
              <Badge color="gray" variant="outline">{node.tokens.input ?? 0}/{node.tokens.output ?? 0} tok</Badge>
            )}
          </Group>
          {node.summary && <Text size="sm" c={isSummary ? undefined : 'dimmed'}>{node.summary}</Text>}
          {!isJudge && !isReco && Array.isArray(node.reasoning) && node.reasoning.length > 0 && (
            <div>
              <Text fw={600} size="sm" mb={4}>Reasoning</Text>
              <Stack gap={2}>{node.reasoning.map((s, i) => <Text key={i} size="sm">• {s}</Text>)}</Stack>
            </div>
          )}
          {drillable && (
            <SegmentedControl size="xs" value={view} onChange={setView}
              data={[{ label: 'What was used (then)', value: 'then' }, { label: 'State now', value: 'now' }]} />
          )}
          {isPricing && (
            <>
              <Divider label="Why this price limit" />
              <Anchor href="/sales/funnel/documentation#pricing" target="_blank" rel="noopener noreferrer" size="xs">
                How is pricing calculated? →
              </Anchor>
              {Array.isArray(shown) && shown.some((l) => l && l.scenario != null) ? (
                <>
                  {/* Normally the SELECTED line only (ExplainGraph narrows it). If the line couldn't be
                      identified it falls back to all of them — hence the rule between ladders. Always name
                      the part, so it's clear which line this ladder belongs to. */}
                  {shown.filter((l) => l && l.scenario != null).map((l, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <Divider my="sm" size="sm" />}
                      <PricingExplainer line={l} showPart />
                    </React.Fragment>
                  ))}
                  <Divider label="Signals detail" />
                </>
              ) : (
                <Text size="sm" c="dimmed">
                  Detailed pricing signals weren't captured for this enrichment. Re-enrich the deal to see
                  the full scenario, basis, and limit breakdown.
                </Text>
              )}
            </>
          )}
          {isScore && shown && shown.priority_factors && (
            <>
              <Divider label="How the score was calculated" />
              <ScoreExplainer scores={shown} />
              <Divider label="Scores detail" />
            </>
          )}
          {isSummary && (
            <>
              <Divider label={key === 'customer_brief' ? 'About the customer' : 'About this line'} />
              <Text size="xs" c="dimmed">
                {key === 'customer_brief'
                  ? 'A fast, lightweight model reads the CUSTOMER record — and no lines at all, so it cannot mention a part. The same brief appears on every line of every deal this customer sends us.'
                  : 'A fast, lightweight model reads THIS line — and no others, so it cannot compare it to a sibling part or quote a deal total. Written for you; the judge does not read it.'}
              </Text>
              <Anchor href="/sales/funnel/documentation#summarize" target="_blank" rel="noopener noreferrer" size="xs">
                How does this work? →
              </Anchor>
            </>
          )}
          {isJudge && shown && (shown.recommended_action != null || shown.priority_adjustment != null || shown.treatment_stance != null) && (
            <>
              <Divider label="The AI judgment" />
              <Anchor href="/sales/funnel/documentation#judge" target="_blank" rel="noopener noreferrer" size="xs">
                How does this work? →
              </Anchor>
              <JudgeExplainer data={shown} model={node.model} />
              <Divider label="Judgment detail" />
            </>
          )}
          {isReco && shown && (shown.headline != null || Array.isArray(shown.considerations) || shown.warranted != null) && (
            <>
              <Divider label="The recommendation" />
              <Anchor href="/sales/funnel/documentation#nymeria" target="_blank" rel="noopener noreferrer" size="xs">
                How does this work? →
              </Anchor>
              <RecoExplainer data={shown} model={node.model} />
              <Divider label="Recommendation detail" />
            </>
          )}
          {(isData || isGuidance) && (
            <Anchor href={`/sales/funnel/documentation#${isGuidance ? 'guidance' : 'data'}`} target="_blank" rel="noopener noreferrer" size="xs">
              {isGuidance ? 'Learn about guidance →' : 'Learn about the data inputs →'}
            </Anchor>
          )}
          {!isPricing && !isScore && !isSummary && !isJudge && !isReco && <Divider label={view === 'now' ? 'Now (point-in-time)' : 'What was used'} />}
          {!isSummary && <RenderData value={shown} />}
        </Stack>
      )}
    </Drawer>
  );
}

// Business-friendly view of Nymeria's recommendation — the sales agent's final call (quote / hold / pass)
// made on top of Vesper's never-below. Handles the gated/skipped case (warranted=false) too.
function RecoExplainer({ data, model }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const cardBg = dark ? 'var(--mantine-color-dark-6)' : '#fff';
  const list = (v) => (Array.isArray(v) ? v.filter((x) => x != null && String(x).trim() !== '') : []);
  const label = (t) => <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>{t}</Text>;

  if (data?.warranted === false) {
    return (
      <Stack gap={8}>
        <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #868e96' }}>
          <Text fw={700} size="sm">Skipped — not warranted</Text>
          <Text size="xs" c="dimmed">
            The AI Sales Agent makes a recommendation only when the judge calls for a quote (quote now / quote with review).
            {data?.recommended_action ? ` The judge chose “${String(data.recommended_action).replace(/_/g, ' ')}”.` : ''} No model was called.
          </Text>
        </Paper>
        {data?.headline && <Text size="sm">{data.headline}</Text>}
      </Stack>
    );
  }

  const considerations = list(data?.considerations);
  const reasoning = list(data?.reasoning);
  const cited = list(data?.guidance_applied);
  return (
    <Stack gap={8}>
      <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #ae3ec9' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700} size="sm">AI Sales Agent — the final call</Text>
          {model && <Badge color="grape" variant="light">{model}</Badge>}
        </Group>
        <Text size="xs" c="dimmed">
          Sees the whole board and leadership's guidance, and decides on top of the pipeline's never-below — quote, hold, or pass.
        </Text>
      </Paper>
      {data?.headline && (
        <Paper withBorder p="sm" radius="md" style={{ background: cardBg }}>
          {label('The decision')}
          <Text size="sm" fw={700} mt={2}>{data.headline}</Text>
          {data?.guidance && <Text size="sm" c="dimmed" mt={4}>{data.guidance}</Text>}
        </Paper>
      )}
      {considerations.length > 0 && (
        <div>{label('What drove it')}<Stack gap={2} mt={2}>{considerations.map((s, i) => <Text key={i} size="sm">• {s}</Text>)}</Stack></div>
      )}
      {reasoning.length > 0 && (
        <div>{label('How it got there')}<Stack gap={2} mt={2}>{reasoning.map((s, i) => <Text key={i} size="sm">• {s}</Text>)}</Stack></div>
      )}
      {cited.length > 0 && (
        <div>{label('Guidance applied')}<Group gap={4} mt={4}>{cited.map((g, i) => <Badge key={i} size="sm" color="pink" variant="light">{String(g)}</Badge>)}</Group></div>
      )}
      <Text size="xs" c="dimmed" fs="italic">Decides on top of the pipeline's never-below — and never under it. A minimum sell price is absolute.</Text>
    </Stack>
  );
}

// Business-friendly view of the AI judgment (the judge). The judge applies leadership's guidance to nuance
// the deterministic score: a bounded nudge, a stance, a recommended action, cited guidance, reasoning
// steps, flags, and an adjudication (review) signal.
const ACTION_META = {
  quote_now: { label: 'Quote now', color: 'green', gloss: 'Fillable, good customer, nothing to check.' },
  quote_with_review: { label: 'Quote with review', color: 'teal', gloss: 'Fillable, but verify something first (hold, thin margin, pricing).' },
  hold_for_sourcing: { label: 'Hold for sourcing', color: 'orange', gloss: 'Not in stock, but worth sourcing.' },
  request_more_info: { label: 'Request more info', color: 'yellow', gloss: 'RFQ unclear — missing condition or ambiguous part number.' },
  deprioritize: { label: 'Deprioritize', color: 'gray', gloss: 'Low value, no stock, do-not-quote, or already handled.' },
};
const STANCE_COLOR = { prioritize: 'green', standard: 'blue', cautious: 'orange', decline: 'red' };
function JudgeExplainer({ data, model }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const cardBg = dark ? 'var(--mantine-color-dark-6)' : '#fff';
  const adj = Number(data?.priority_adjustment);
  const hasAdj = data?.priority_adjustment != null && !Number.isNaN(adj);
  const adjColor = !hasAdj || adj === 0 ? '#868e96' : adj > 0 ? '#2f9e44' : '#e03131';
  const adjText = hasAdj ? (adj > 0 ? `+${adj}` : `${adj}`) : '—';
  const action = ACTION_META[data?.recommended_action]
    || (data?.recommended_action ? { label: String(data.recommended_action).replace(/_/g, ' '), color: 'gray', gloss: null } : null);
  const stance = data?.treatment_stance;
  const list = (v) => (Array.isArray(v) ? v.filter((x) => x != null && String(x).trim() !== '') : []);
  const reasoning = list(data?.reasoning);
  const cited = list(data?.guidance_applied);
  const flags = list(data?.flags);
  const label = (t) => <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: 0.3 }}>{t}</Text>;
  return (
    <Stack gap={8}>
      <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #7048e8' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700} size="sm">AI judgment · the second AI step</Text>
          {model && <Badge color="violet" variant="light">{model}</Badge>}
        </Group>
        <Text size="xs" c="dimmed">
          Applies leadership's guidance and the judgment the numbers can't see. It <b>nuances</b> the
          score — it doesn't re-score.
        </Text>
      </Paper>
      <Paper withBorder p="xs" radius="md" style={{ background: cardBg }}>
        <Group justify="space-between" wrap="nowrap">
          <div>
            {label('Judge nudge')}
            <Text size="xs" c="dimmed">bounded adjustment on top of the base score → final Priority</Text>
          </div>
          <span style={{ fontWeight: 800, fontSize: 20, color: adjColor, background: `${adjColor}22`,
            borderRadius: 8, padding: '2px 12px', whiteSpace: 'nowrap' }}>{adjText}</span>
        </Group>
      </Paper>
      <Group gap={20} align="flex-start">
        {stance && <div>{label('Treatment stance')}<div><Badge mt={2} color={STANCE_COLOR[stance] || 'gray'} variant="light">{stance}</Badge></div></div>}
        {action && <div>{label('Recommended action')}<div><Badge mt={2} color={action.color} variant="filled">{action.label}</Badge></div></div>}
      </Group>
      {action?.gloss && <Text size="xs" c="dimmed">{action.gloss}</Text>}
      {data?.priority_rationale && (<div>{label('Why')}<Text size="sm" mt={2}>{data.priority_rationale}</Text></div>)}
      {reasoning.length > 0 && (
        <div>{label('How it got there')}<Stack gap={2} mt={2}>{reasoning.map((s, i) => <Text key={i} size="sm">• {s}</Text>)}</Stack></div>
      )}
      {cited.length > 0 && (
        <div>{label('Guidance applied')}<Group gap={4} mt={4}>{cited.map((g, i) => <Badge key={i} size="sm" color="pink" variant="light">{String(g)}</Badge>)}</Group></div>
      )}
      {flags.length > 0 && (
        <div>{label('Flags')}<Group gap={4} mt={4}>{flags.map((f, i) => <Badge key={i} size="sm" color="orange" variant="light">{String(f)}</Badge>)}</Group></div>
      )}
      {data?.adjudication_needed && (
        <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #e8590c' }}>
          <Text size="sm" fw={600} c="orange">Flagged for review</Text>
          {data?.adjudication_reason && <Text size="xs" c="dimmed">{data.adjudication_reason}</Text>}
        </Paper>
      )}
      <Text size="xs" c="dimmed" fs="italic">
        The recommended action decides what happens next: the AI Sales Agent drafts a recommendation only on
        “quote now” or “quote with review.”
      </Text>
    </Stack>
  );
}

// (SummaryExplainer deleted.) It rendered `customer_summary` + `opportunity_summary` + deal-level flags in one
// card and told the reader "This brief is what the judge reads next" — untrue since the deal narrative was
// taken away from the per-line judge. Both halves have been re-grained: the customer brief and the line
// summary are now separate nodes carrying their prose directly, so there is nothing structured left to render.

// Business-friendly "why this price" decision flow for a pricing line:
// scenario → the basis waterfall (chosen path highlighted) → the binding never-below → hand-off to Nymeria.
// A live MIN SELL PRICE short-circuits all of it: it overrides the SSP and cannot be discounted.
const priceFmt = (v) => (v == null || v === '' || Number.isNaN(Number(v))) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;
function PricingExplainer({ line, showPart }) {
  const scheme = useComputedColorScheme('light');
  const chosenBg = scheme === 'dark' ? 'rgba(247,103,7,0.18)' : '#fff4e6';
  if (!line) return null;
  const sc = Number(line.scenario) || 3;
  const scColor = sc === 1 ? '#2f9e44' : sc === 2 ? '#e8590c' : '#e03131';
  // Plain English, not "S2". Prefer the stored label; fall back to the scenario number so enrichments
  // written before the wording changed still read correctly.
  const SCENARIO_TEXT = { 1: 'good pricing', 2: 'expired pricing', 3: 'no pricing' };
  const scText = (line.scenario_label && !/^non-expired|^expired\/stale/i.test(line.scenario_label))
    ? line.scenario_label
    : (SCENARIO_TEXT[sc] || 'no pricing');
  const scWhy = sc === 1 ? 'A governed sell price is on file for this condition and is still in date.'
    : sc === 2 ? 'A governed sell price is on file but has EXPIRED — verify before quoting.'
    : 'No governed sell price for this condition; pricing falls back to a comp sale or cost + margin.';
  const pos = (v) => Number(v) > 0;
  // A live MIN SELL PRICE **IS** the price — a person's current valuation of THIS unit, which overrides the
  // part's (often stale) SSP and is not discounted. So it is the first rung, and when set it wins outright,
  // INCLUDING when it is below the SSP (76 of the 88 min-priced units are). Only rendered when one exists
  // (~0.1% of units), because a row reading "min sell price — none" on every other line would be noise.
  const minLive = pos(line.min_sell_price);   // a min sell price never expires — set means valid
  const rows = [
    ...(pos(line.min_sell_price) ? [{
      key: 'min_sell',
      label: 'Min sell price — priced on this unit',
      value: line.min_sell_price,
      note: 'this IS the price · overrides the SSP · not discounted · never expires',
      why: 'not set',
    }] : []),
    { key: 'ssp_dos', label: 'Governed SSP × DOS', value: line.ssp_dos_price,
      note: pos(line.ssp) ? `SSP ${priceFmt(line.ssp)}${pos(line.dos_discount_pct) ? ` − ${line.dos_discount_pct}% DOS` : ''}` : null,
      why: minLive ? 'no discount allowed' : (line.ssp_expired ? 'SSP expired' : (pos(line.ssp) ? null : 'no SSP')) },
    { key: 'comp', label: 'Recent comp sale', value: line.comp_price,
      note: line.comp_condition ? `${line.comp_condition} sale, last 3 yr` : null, why: 'no recent sale' },
    { key: 'repair_implied', label: 'Repair-implied AR limit', value: line.repair_implied_limit,
      note: pos(line.avg_repair_cost) ? `SV/OH sale − repair ${priceFmt(line.avg_repair_cost)}` : null, why: 'n/a' },
    { key: 'cost_plus', label: 'Cost + margin limit', value: line.cost_plus_limit,
      note: pos(line.unit_cost) ? `unit cost ${priceFmt(line.unit_cost)}` : null, why: 'no cost' },
  ];
  const arrow = <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1 }}>▼</Text>;
  return (
    <Stack gap={6}>
      {showPart && (
        <Group gap={6} align="center">
          <Text size="sm" fw={700}>{line.part_number}</Text>
          <Badge size="xs" variant="light" color="gray">{line.condition_code || 'no cond'}</Badge>
        </Group>
      )}
      <Paper withBorder p="xs" radius="md" style={{ borderLeft: `4px solid ${scColor}` }}>
        <Text fw={700} size="sm" style={{ color: scColor, textTransform: 'capitalize' }}>{scText}</Text>
        <Text size="xs" c="dimmed">{scWhy}</Text>
        {/* NAME THE UNIT. Every number in this drawer — the cost, the DOS, the min sell price — belongs to one
            physical item (the FIFO-oldest we would ship), not to the part in the abstract. */}
        <Text size="xs" c="dimmed" mt={2}>
          SSP {priceFmt(line.ssp)}{line.ssp != null && (line.ssp_expired ? ' (expired)' : ' (valid)')} · DOS {line.dos ?? '—'}d
          {line.ship_serial ? ` · S/N ${line.ship_serial}` : ''}
        </Text>
      </Paper>
      {minLive && (
        <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #e03131' }}>
          <Group justify="space-between" wrap="nowrap">
            <Text fw={700} size="sm" c="red.6">Min sell price — {priceFmt(line.min_sell_price)}</Text>
            <Badge size="xs" color="red" variant="light">no discount</Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Priced on this specific unit{line.ship_serial ? ` (S/N ${line.ship_serial})` : ''}, not on the part —
            so <b>this IS the price</b>, and it <b>overrides the SSP</b>{pos(line.ssp) ? ` of ${priceFmt(line.ssp)}` : ''}.
            The days-on-shelf discount does <b>not</b> apply: someone looked at this item, at its age, and priced
            it. Knocking a further 15% off for being old would double-count the very thing they already
            allowed for.
          </Text>
        </Paper>
      )}
      {arrow}
      <Text size="xs" c="dimmed">Pick the price basis (first available wins):</Text>
      {/* basis === 'none' means EVERY rung below failed. Without this the drawer just showed four
          unhighlighted rows and left the rep to work out that nothing had been chosen — and why. */}
      {line.basis === 'none' && (
        <Paper withBorder p="xs" radius="md" style={{ borderLeft: '4px solid #e03131' }}>
          <Text size="sm" fw={700} c="red.6">No reference price — every basis failed</Text>
          <Text size="xs" c="dimmed">
            No in-date list price for this condition, no recent sale of it, no repair-implied limit, and no cost
            to build one from. We have nothing to anchor on, so this line has to be priced by judgment — check
            the part history and vendor RFQs before quoting.
          </Text>
        </Paper>
      )}
      <Stack gap={3}>
        {rows.map((r) => {
          const chosen = r.key === line.basis;
          const avail = pos(r.value);
          return (
            <Group key={r.key} justify="space-between" wrap="nowrap" align="flex-start"
              style={{ padding: '4px 8px', borderRadius: 6, opacity: avail ? 1 : 0.5,
                       background: chosen ? chosenBg : 'transparent', border: `1px solid ${chosen ? '#f76707' : 'transparent'}` }}>
              <Group gap={6} wrap="nowrap" align="flex-start">
                <Text size="sm" style={{ width: 12 }}>{chosen ? '✓' : avail ? '•' : '○'}</Text>
                <div>
                  <Text size="sm" fw={chosen ? 700 : 500}>{r.label}{chosen ? '  ← chosen' : ''}</Text>
                  {r.note && <Text size="xs" c="dimmed">{r.note}</Text>}
                </div>
              </Group>
              <Text size="sm" fw={chosen ? 700 : 400} c={avail ? undefined : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                {avail ? priceFmt(r.value) : (r.why || '—')}
              </Text>
            </Group>
          );
        })}
      </Stack>
      {arrow}
      {/* NOT "the floor". The business says "the floor" and means the SSP, so calling the cost-derived
          never-below "the floor" pointed the same word at two different numbers. This is the ECONOMIC
          LIMIT — below it the deal stops making sense for us. It is a signal, not a rule. The one rule is
          the min sell price above. */}
      <Paper withBorder p="xs" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Text fw={600} size="sm">{minLive ? 'The price — set on this unit' : 'Economic limit — below this we lose'}</Text>
          <Text fw={700} size="sm">{priceFmt(minLive ? line.min_sell_price : line.economic_limit)}</Text>
        </Group>
        <Text size="xs" c="dimmed">
          {minLive
            ? 'the min sell price governs — it is a current valuation of this unit, so it stands whether it lands above or below the part\u2019s list price'
            : <>the higher of cost + margin {priceFmt(line.cost_plus_limit)}
              {pos(line.repair_implied_limit) ? ` and the repair-implied limit ${priceFmt(line.repair_implied_limit)}` : ''}</>}
        </Text>
      </Paper>
      {arrow}
      <Paper p="xs" radius="md" style={{ border: '1px dashed #adb5bd' }}>
        <Text size="sm">Price decision → <b>AI Sales Agent</b> <Text span size="xs" c="dimmed">(judgment over these signals + pricing guidance)</Text></Text>
      </Paper>
      {Array.isArray(line.flags) && line.flags.length > 0 && (
        <Group gap={4}>{line.flags.map((f, i) => <Badge key={i} size="xs" color="orange" variant="light">{String(f).replace(/_/g, ' ')}</Badge>)}</Group>
      )}
    </Stack>
  );
}

// Business-friendly "how the priority score was built" breakdown for the score. The deterministic score is
// clamp(fill_base + value + customer_bonus + rating_bonus + freshness, 0..100). Scoring is PER LINE, and
// customer/rating/freshness are deal-level — so `value` (the line's margin dollars) is the term that
// actually ranks one line of a deal against another. We show each additive factor with a plain-English
// reason and a proportional bar, then the capped total, then the hand-off to the judge (which may nudge it
// ±10). Reads numeric_scores_json straight from the node.
const COVERAGE_NOTE = {
  have_all: 'We stock the requested condition — clears the fill gate',
  have_none: 'Not in stock in the requested condition — lowest fill weight',
  unknown: 'Coverage could not be determined',
};
const round1 = (v) => Math.round(Number(v) * 10) / 10;
function ScoreExplainer({ scores }) {
  const scheme = useComputedColorScheme('light');
  const dark = scheme === 'dark';
  const rowBg = dark ? 'var(--mantine-color-dark-6)' : '#f8f9fa';
  const trackBg = dark ? 'var(--mantine-color-dark-4)' : '#e9ecef';
  // `scores` is now THIS LINE's LineScore — not the deal's NumericScores. The deal object carried
  // cross-line aggregates (priority = MAX line, margin = SUM over lines, coverage_pct = "% of LINES in
  // stock", factors = the DRIVING line's), so every line of a multi-line deal used to render an identical,
  // and mostly wrong, breakdown. LineScore fields: coverage, fillable, margin_dollars, margin_pct,
  // cost_basis, payback_pct, priority_score, priority_factors, part_number, condition_code.
  const s = scores || {};
  const f = s.priority_factors || {};
  const num = (v) => (v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
  const total = num(s.priority_score);
  const fillable = s.fillable === true;
  const marginOf = (v) => (v == null ? null : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} est. gross margin`);
  const basis = s.cost_basis ? String(s.cost_basis).trim().toUpperCase() : null;
  const rows = [
    { key: 'fill_base', label: 'In-stock coverage', pts: num(f.fill_base),
      note: COVERAGE_NOTE[s.coverage] || 'Fill weight',
      extra: null },
    { key: 'value', label: 'Line value', pts: num(f.value),
      note: num(f.value) > 0
        ? 'What this line is worth — the only factor that separates the lines of one deal'
        : 'We cannot fill this line, so it earns no value points',
      extra: [marginOf(s.margin_dollars), basis ? `cost basis: ${basis}` : null].filter(Boolean).join(' · ') || null },
    // Customer / rating / freshness are DEAL-level: identical for every line of this deal, by definition.
    { key: 'customer_bonus', label: 'Customer priority', pts: num(f.customer_bonus),
      note: 'Deal-level — the same for every line of this RFQ', extra: null },
    { key: 'rating_bonus', label: 'Account rating', pts: num(f.rating_bonus),
      note: num(f.rating_bonus) > 0 ? 'A/B-rated account bonus (deal-level)' : 'No A/B rating bonus', extra: null },
    { key: 'freshness', label: 'RFQ freshness', pts: num(f.freshness),
      note: 'Newer RFQs score higher; decays with age (deal-level)', extra: null },
  ];
  const rawSum = rows.reduce((a, r) => a + r.pts, 0);
  const maxPts = Math.max(1, ...rows.map((r) => r.pts));
  const arrow = <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1 }}>▼</Text>;
  const scColor = fillable ? '#2f9e44' : '#868e96';
  return (
    <Stack gap={6}>
      <Paper withBorder p="xs" radius="md" style={{ borderLeft: `4px solid ${scColor}` }}>
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700} size="sm">Priority score {total} / 100</Text>
          <Badge color={fillable ? 'green' : 'gray'} variant="light">{fillable ? 'Can fill from stock' : 'Cannot fill from stock'}</Badge>
        </Group>
        <Text size="xs" c="dimmed">Ranks the line in the worklist. The factors add up, then the total is capped to 0–100.</Text>
        <Group gap="md" mt={4}>
          <Anchor href="/sales/funnel/documentation#priority-score" target="_blank" rel="noopener noreferrer" size="xs">
            How is this calculated? →
          </Anchor>
          {/* Line value is banded on the estimated GROSS margin, which depends on the unit's cost basis
              (paid-off airframe vs one still being repaid). Without that, the value points look arbitrary. */}
          <Anchor href="/sales/funnel/documentation#margin" target="_blank" rel="noopener noreferrer" size="xs">
            How is margin calculated? →
          </Anchor>
        </Group>
      </Paper>
      {/* No table of the deal's other lines. This drawer explains ONE line — the one you clicked. The other
          lines are judged independently and are none of this line's business; the deal simply takes the
          highest of them. */}
      {arrow}
      <Text size="xs" c="dimmed">
        How this line&apos;s score is built up (fill is the gate, then what the line is worth, then who the
        customer is and how fresh the RFQ is):
      </Text>
      <Stack gap={3}>
        {rows.map((r) => (
          <div key={r.key} style={{ padding: '4px 8px', borderRadius: 6, background: rowBg }}>
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={500}>{r.label}</Text>
                <Text size="xs" c="dimmed">{r.note}{r.extra ? ` · ${r.extra}` : ''}</Text>
              </div>
              <Text size="sm" fw={700} c={r.pts > 0 ? undefined : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>
                {r.pts > 0 ? '+' : ''}{round1(r.pts)}
              </Text>
            </Group>
            <div style={{ height: 4, borderRadius: 2, background: trackBg, marginTop: 4 }}>
              <div style={{ height: 4, borderRadius: 2, width: `${Math.min(100, (r.pts / maxPts) * 100)}%`, background: scColor }} />
            </div>
          </div>
        ))}
      </Stack>
      {arrow}
      <Paper withBorder p="xs" radius="md">
        {(() => {
          const capped = rawSum > 100 || rawSum < 0;
          return (
            <>
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600} size="sm">Base priority score{capped ? ' (capped 0–100)' : ''}</Text>
                <Text fw={700} size="sm">{total}</Text>
              </Group>
              {capped
                ? <Text size="xs" c="dimmed">Raw sum {round1(rawSum)} → capped to the 0–100 range = {total}.</Text>
                : (round1(rawSum) !== total && <Text size="xs" c="dimmed">Sum {round1(rawSum)}, rounded to {total}.</Text>)}
            </>
          );
        })()}
      </Paper>
      {arrow}
      <Paper p="xs" radius="md" style={{ border: '1px dashed #adb5bd' }}>
        <Text size="sm">Base score → <b>the judge</b> may nudge it ±10 <Text span size="xs" c="dimmed">(a bounded, cited-guidance adjustment)</Text> → the <b>Priority</b> the rep sees.</Text>
      </Paper>
    </Stack>
  );
}

// Render a captured JSON value as a table (array-of-objects → grid; object → key/value; primitives → chips).
// The audience is non-technical, so we never show raw JSON — nested values collapse to a compact hint.
const isObj = (v) => v != null && typeof v === 'object' && !Array.isArray(v);
const prettyKey = (k) => String(k).replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
const cell = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) return v.every((x) => !isObj(x) && !Array.isArray(x)) ? v.join(', ') : `${v.length} item(s)`;
  if (isObj(v)) return Object.keys(v).length ? '{…}' : '—';
  return String(v);
};

function RenderData({ value, depth = 0 }) {
  if (value == null) return <Text c="dimmed" size="sm">No data.</Text>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <Text c="dimmed" size="sm">None.</Text>;
    if (value.every(isObj)) {
      const cols = [...new Set(value.flatMap((o) => Object.keys(o)))];
      return (
        <ScrollArea type="auto">
          <Table striped withTableBorder withColumnBorders fz="xs" stickyHeader>
            <Table.Thead><Table.Tr>{cols.map((c) => <Table.Th key={c} style={{ whiteSpace: 'nowrap' }}>{prettyKey(c)}</Table.Th>)}</Table.Tr></Table.Thead>
            <Table.Tbody>
              {value.map((o, i) => <Table.Tr key={i}>{cols.map((c) => <Table.Td key={c}>{cell(o[c])}</Table.Td>)}</Table.Tr>)}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      );
    }
    return <Group gap={4}>{value.map((v, i) => <Badge key={i} variant="light" color="gray">{cell(v)}</Badge>)}</Group>;
  }

  if (isObj(value)) {
    const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
    if (entries.length === 0) return <Text c="dimmed" size="sm">No data.</Text>;
    return (
      <Table striped withTableBorder fz="xs">
        <Table.Tbody>
          {entries.map(([k, v]) => (
            <Table.Tr key={k}>
              <Table.Td style={{ fontWeight: 600, width: '38%', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{prettyKey(k)}</Table.Td>
              <Table.Td>
                {depth < 1 && (isObj(v) || (Array.isArray(v) && v.some(isObj)))
                  ? <RenderData value={v} depth={depth + 1} />
                  : cell(v)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  return <Text size="sm">{cell(value)}</Text>;
}
